import { z } from 'zod';
import { hexColorSchema, idSchema, isoDateTimeSchema } from '../../common.js';
/**
 * Auth & household-member module (WS-2). Identity lives in Postgres (`users`, `sessions`,
 * `invites` — see `migrations/auth/`); these are the wire-format contracts shared between
 * `apps/server/src/modules/auth` and `apps/web/src/modules/auth`.
 */
/**
 * Member colour palette (ROADMAP §1.3 `color`). Expanded from 8 to 16 on 2026-08-01 —
 * usability testing had two members (Dan, Faye) land on near-identical colours because the
 * old set was too small for a growing household. All 16 are Tailwind 500 shades (same
 * convention as the original 8) spaced evenly around the hue wheel so any two picks stay
 * visually distinguishable, and each clears 4.5:1 contrast in every shipped theme (verified
 * the same way the 2026-08-01 theme-play entry checked member colours as text). Any store
 * (`seed:owner`, `registerRequestSchema`) that validates against this array picks up the
 * larger set for free — there is exactly one list.
 */
export const MEMBER_COLORS = [
    '#ef4444', // red
    '#f97316', // orange
    '#f59e0b', // amber
    '#eab308', // yellow
    '#84cc16', // lime
    '#22c55e', // green
    '#10b981', // emerald
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#0ea5e9', // sky
    '#3b82f6', // blue
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#d946ef', // fuchsia
    '#ec4899', // pink
    '#f43f5e', // rose
];
export const memberColorSchema = z.enum(MEMBER_COLORS);
export const AVATAR_EMOJI_CATEGORIES = [
    {
        id: 'faces',
        label: 'Faces',
        emojis: [
            '😀',
            '😄',
            '😊',
            '😎',
            '🤩',
            '😉',
            '😇',
            '🥳',
            '😴',
            '🤗',
            '🤔',
            '😜',
            '🥸',
            '🤠',
            '👻',
            '🤖',
            '👽',
            '🥰',
        ],
    },
    {
        id: 'animals',
        label: 'Animals',
        emojis: [
            '🐶',
            '🐱',
            '🦊',
            '🐼',
            '🐨',
            '🐯',
            '🦁',
            '🐵',
            '🐰',
            '🦄',
            '🐧',
            '🦉',
            '🐸',
            '🐢',
            '🐬',
            '🐙',
            '🦋',
            '🐝',
            '🐴',
            '🦖',
        ],
    },
    {
        id: 'food',
        label: 'Food',
        emojis: [
            '🍕',
            '🍔',
            '🌮',
            '🍣',
            '🍦',
            '🎂',
            '🍪',
            '🍩',
            '🍎',
            '🍉',
            '🍒',
            '🥑',
            '🍿',
            '🍭',
            '☕',
            '🧀',
            '🍓',
            '🥐',
        ],
    },
    {
        id: 'activities',
        label: 'Activities',
        emojis: [
            '⚽',
            '🏀',
            '🎸',
            '🎨',
            '📚',
            '🎮',
            '🚴',
            '🛹',
            '🚀',
            '🏆',
            '🎲',
            '🧩',
            '🎯',
            '🎳',
            '🏓',
            '🎧',
        ],
    },
    {
        id: 'nature',
        label: 'Nature',
        emojis: ['☀️', '🌙', '⭐', '🌈', '☁️', '❄️', '🔥', '⚡', '🌸', '🌳', '🌵', '🍀', '🌊', '🌻'],
    },
    {
        id: 'objects',
        label: 'Objects',
        emojis: ['👑', '💎', '🎈', '🎁', '📷', '🧸', '👓', '☂️', '🔑', '💡', '⚓', '🧭'],
    },
];
/** Flat list of every curated emoji, in category order — kept for callers that don't need
 * the category grouping (e.g. validation, "is this one of the curated options" checks). */
export const AVATAR_EMOJI_OPTIONS = AVATAR_EMOJI_CATEGORIES.flatMap((category) => category.emojis);
export const avatarEmojiSchema = z.string().trim().min(1).max(8);
export const emailSchema = z.string().trim().toLowerCase().email().max(320);
export const passwordSchema = z.string().min(8).max(200);
export const nameSchema = z.string().trim().min(1).max(100);
/** Safe, public projection of a `users` row — never includes `password_hash`. */
export const publicUserSchema = z.object({
    id: idSchema,
    name: nameSchema,
    email: emailSchema,
    color: hexColorSchema,
    avatarEmoji: avatarEmojiSchema.optional(),
    createdAt: isoDateTimeSchema,
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const loginResponseSchema = z.object({ user: publicUserSchema });
export const meResponseSchema = z.object({ user: publicUserSchema.nullable() });
export const registerRequestSchema = z.object({
    inviteToken: z.string().min(1).max(512),
    name: nameSchema,
    email: emailSchema,
    password: passwordSchema,
    color: memberColorSchema,
    avatarEmoji: avatarEmojiSchema,
});
export const registerResponseSchema = z.object({ user: publicUserSchema });
/**
 * `PATCH /api/auth/me` (2026-08-01, profile editor): a member can rename themselves and
 * change colour/avatar after registration — usability testing found no way to do either.
 * All fields optional, but at least one must be present (an empty PATCH is a client bug,
 * not a no-op).
 */
export const updateProfileRequestSchema = z
    .object({
    name: nameSchema.optional(),
    color: memberColorSchema.optional(),
    avatarEmoji: avatarEmojiSchema.optional(),
})
    .strict()
    .refine((data) => Object.keys(data).length > 0, { message: 'empty_update' });
export const updateProfileResponseSchema = z.object({ user: publicUserSchema });
/** `createInviteRequestSchema` is intentionally empty today; kept as a boundary contract
 * so an expiry override can be added later without an untyped body. */
export const createInviteRequestSchema = z.object({}).strict();
export const createInviteResponseSchema = z.object({
    token: z.string().min(1),
    expiresAt: isoDateTimeSchema,
});
/** Non-sensitive colour hint for an existing household member — enough for a not-yet-
 * authenticated invitee's colour picker to nudge away from taken colours, nothing more. */
export const memberColorHintSchema = z.object({
    name: nameSchema,
    color: hexColorSchema,
    avatarEmoji: avatarEmojiSchema.optional(),
});
export const inviteStatusSchema = z.object({
    valid: z.boolean(),
    reason: z.enum(['not_found', 'expired', 'used']).optional(),
    expiresAt: isoDateTimeSchema.optional(),
    /** Present only when `valid`: the household's current members, for taken-colour hints. */
    members: z.array(memberColorHintSchema).optional(),
});
export const authErrorSchema = z.object({ error: z.string() });
//# sourceMappingURL=index.js.map